import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom';
import {
  AdaptiveFilter,
  AdaptiveSearchInput,
  Box,
  CalendarIcon,
  CalendarPickerIcon,
  capitalizeFirstLetter,
  DateRangeDropdown,
  FlexList,
  PaginationBar,
  SelectDropdown,
  Text,
  usePaginationParams,
  useResponsive,
} from '@common-fe/common-fe';

import { ALL_OPTION } from '@/common/constants';
import regexp from '@/common/regexp';
import ROUTES from '@/common/routes';
import { ListItemBase, OptionKey } from '@/common/types';
import { useActivitiesDatesRange } from '@/modules/employee/EnrollmentDetails/ActivitiesList/hooks';
import useGetClaimStatusesQuery from '@/modules/employee/EnrollmentDetails/queries/useGetClaimStatuses.query';

import { CardTransactionItem } from '../../TransactionsList/CardTransactionItem';
import { EmptyTransactionsListPlaceholder } from '../../TransactionsList/EmptyTransactionsListPlaceholder';

import useAdaptiveFilter from './hooks/useAdaptiveFilters';
import useCardTransactionsList from './hooks/useCardTransacrionList';

const stateAllToggling = (statuses: OptionKey[]) => {
  const minStatusesLength = 2;
  const isStatusAllSelected = statuses[statuses.length - 1] === ALL_OPTION.value;
  if (statuses.length === 0 || isStatusAllSelected) {
    return [ALL_OPTION.value];
  }
  const isStatusAllChangedToAnother =
    statuses.length === minStatusesLength && statuses[0] === ALL_OPTION.value;
  if (isStatusAllChangedToAnother) {
    return statuses.filter((status) => status !== ALL_OPTION.value);
  }
  return statuses;
};

const HEADERS: ListItemBase[] = [
  {
    key: 'provider',
    title: '',
    flex: 6,
  },
  {
    key: 'amount',
    title: 'Amount',
    flex: 2,
  },
  {
    key: 'status',
    title: 'Status',
    flex: 2,
  },
  {
    key: 'statusDate',
    title: 'Status Date',
    flex: 1.5,
  },
];

export const CardTransactionsList = () => {
  const [statuses, setStatuses] = useState<OptionKey[]>([]);
  const [initialData, setInitialData] = useState(false);
  const [subLoading, setSubLoading] = useState(false);
  const { push } = useHistory();
  const { onChangeDates, formattedDates } = useActivitiesDatesRange();
  const { statusesWithoutCanceled } = useGetClaimStatusesQuery();
  const { id: employeeId } = useParams<{ id: string }>();

  const { page, setPage, setPerPage, perPage, searchString, setSearchString, currentSearch } =
    usePaginationParams();

  useEffect(() => {
    setPage(1);
  }, [setPage, currentSearch, formattedDates.length, statuses]);

  useEffect(() => {
    setSubLoading(true);
    const timeout = setTimeout(() => {
      setSubLoading(false);
    }, 500);
    return () => clearTimeout(timeout);
  }, [page, currentSearch, formattedDates.length, statuses]);

  const statusesOptions = useMemo(() => {
    const preparedStatuses = statusesWithoutCanceled.map((status) => {
      const preparedStatus = capitalizeFirstLetter(status.name).replace(regexp.DASH_SYMBOL, ' ');
      return {
        key: status.name,
        value: capitalizeFirstLetter(status.name),
        title: preparedStatus,
      };
    });
    return [ALL_OPTION, ...preparedStatuses];
  }, [statusesWithoutCanceled]);
  const statusesOptionsWithAll = useMemo(() => {
    return [ALL_OPTION, ...statusesOptions];
  }, [statusesOptions]);
  const statusValues = useMemo(() => stateAllToggling(statuses), [statuses]);
  const hasStatus = useMemo(
    () => statusValues.length > 0 && !statusValues.some((status) => status === ALL_OPTION.value),
    [statusValues]
  );

  const { isLoading, total, data, holderName, removeAllParams } = useCardTransactionsList({
    page: page - 1,
    perPage,
    searchString: currentSearch || '',
    statuses: hasStatus ? statusValues : [],
    startDate: formattedDates[0],
    endDate: formattedDates[1],
  });

  const isAnyInputFilled = useMemo(
    () => searchString.length || statuses.length || formattedDates.length,
    [formattedDates.length, searchString.length, statuses.length]
  );

  const isDataInclude = useMemo(() => !!data.length, [data]);

  const isInitialData = useCallback(
    () => (!data.length && !isAnyInputFilled ? setInitialData(false) : setInitialData(true)),
    [data.length, isAnyInputFilled]
  );
  useEffect(() => {
    isInitialData();
  }, [isInitialData]);
  const { isMobile } = useResponsive();

  useEffect(
    () => () => {
      removeAllParams();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const {
    isFilterVisible,
    setIsFilterVisible,
    handleCancelFilter,
    handleSubmitFilter,
    isFilterApplied,
    handleResetFilter,
    filterTypes,
    setFilterTypes,
    filterDates,
    setFilterDates,
  } = useAdaptiveFilter({
    onSubmit: () => {
      setStatuses(filterTypes);
      onChangeDates(filterDates);
    },
  });

  return (
    <Box
      direction="column"
      data-testid="TransactionsList"
      background="module"
      round="container1Round"
      pad={{ bottom: data ? 'spacing12' : 'spacing24' }}
      margin={{ top: 'spacing24' }}
    >
      <Box
        direction="row"
        data-testid={`activities-list-${holderName}`}
        align="center"
        margin={{ bottom: 'spacing4' }}
        pad={{ horizontal: 'spacing24', top: 'spacing24' }}
      >
        <Box direction="row" style={{ flex: 1 }}>
          <Text size="large" weight="bold">
            {holderName ? `${holderName}'s Activity` : 'Activity'}
          </Text>
        </Box>
        <Box width="largeControl" margin={{ left: 'spacing8' }}>
          <AdaptiveSearchInput
            disabled={!initialData}
            value={searchString}
            hasSearchingHistory
            placeholder="Search"
            onChange={setSearchString}
          />
        </Box>

        {isMobile ? (
          <AdaptiveFilter
            isFilterApplied={isFilterApplied}
            testId="test-filter"
            title="Title"
            onClear={handleResetFilter}
            isVisible={isFilterVisible}
            onSetIsVisible={setIsFilterVisible}
            footer={
              <AdaptiveFilter.Footer
                testId="footer"
                onCancel={handleCancelFilter}
                onSubmit={handleSubmitFilter}
              />
            }
          >
            <AdaptiveFilter.Select
              testId="status"
              isAllMode
              disabled={!initialData}
              label="Status"
              options={statusesOptions}
              value={filterTypes}
              onChangeValue={setFilterTypes}
            />

            <AdaptiveFilter.DateRange
              testId="period"
              label="Period"
              disabled={!initialData}
              value={filterDates}
              onChangeValue={setFilterDates}
            />
          </AdaptiveFilter>
        ) : (
          <>
            <Box width="control" margin={{ left: 'spacing8' }}>
              <SelectDropdown
                disabled={!initialData}
                id="status"
                name="Activity status"
                prefix="Status"
                allMode
                options={statusesOptionsWithAll}
                values={statusValues}
                onChangeValues={setStatuses}
              />
            </Box>

            <Box width="control" margin={{ left: 'spacing8' }}>
              <DateRangeDropdown
                ellipsisMode
                disabled={!initialData}
                prefix="Period:"
                className="period-picker"
                onChange={onChangeDates}
                startDate={formattedDates[0]}
                endDate={formattedDates[1]}
                icon={
                  <Box margin={{ bottom: !initialData ? 'spacing4' : undefined }}>
                    {!initialData ? <CalendarPickerIcon /> : <CalendarIcon color="primaryIcon" />}
                  </Box>
                }
              />
            </Box>
          </>
        )}
      </Box>
      <Box>
        <FlexList
          headers={data ? HEADERS : []}
          rows={[]}
          total={100}
          moreCount={perPage}
          loading={isLoading || subLoading}
          footer={
            <Box direction="column">
              {!isDataInclude && <EmptyTransactionsListPlaceholder title="Activities" />}
              {data.map((item) => (
                <CardTransactionItem
                  key={item?.claimId}
                  category={item.category}
                  title={item.title}
                  amount={item.amount}
                  status={item.status}
                  date={item.date}
                  suppressed={item.suppressed}
                  onPick={() => push(ROUTES.TRANSACTION_DETAILS_CLAIM(employeeId, item?.claimId))}
                />
              ))}
              {isDataInclude && (
                <PaginationBar
                  page={page}
                  total={total || 0}
                  pageSize={perPage}
                  onChangePage={setPage}
                  onChangePageSize={setPerPage}
                />
              )}
            </Box>
          }
        />
      </Box>
    </Box>
  );
};
